import { isRecoverableSyncClose, roleAtLeast, syncPath, type Role } from '@tomo/core/api';
import YProvider from 'y-partyserver/provider';
import type { Awareness } from 'y-protocols/awareness';
import { getBlob, registerRemoteBlobSource } from '../blobs.ts';
import { getIdentity, identityStore } from '../identity.ts';
import { projectFileHashes, type CloudConnection, type PresenceUser, type ProjectSession, type SyncState, type SyncStatus } from '../projects.ts';
import { getRegistryEntry, patchRegistry } from '../registry.ts';
import { api, API_BASE, ApiRequestError } from './api.ts';
import { cloudStore, workspaceById } from './session.ts';

/** Sync states that only change through an explicit action (sign in, upgrade, access granted). */
const TERMINAL: readonly SyncStatus[] = ['upgrade-required', 'forbidden', 'signed-out'];

/** Why the server refused or closed the connection (the WebSocket close reason), for people. */
const CLOSE_REASONS: Record<string, [SyncStatus, string]> = {
  'payment-required': ['upgrade-required', 'This workspace needs a Pro plan to sync.'],
  'workspace-downgraded': ['upgrade-required', 'The workspace is no longer on Pro. Your copy on this device stays editable.'],
  forbidden: ['forbidden', 'You don’t have access to this project.'],
  'member-removed': ['forbidden', 'You were removed from this workspace.'],
  'not-found': ['forbidden', 'This project no longer exists in the cloud.'],
  'project-deleted': ['forbidden', 'This project was deleted from the cloud.'],
  'workspace-deleted': ['forbidden', 'The workspace was deleted.'],
};

/**
 * Connects a cloud project's Y.Doc to Tomo Cloud (Pro): one WebSocket per project to its room on
 * the server. Local IndexedDB persistence stays on, so edits made offline merge automatically when
 * the connection comes back.
 */
export function attachCloud(session: ProjectSession): CloudConnection {
  const setSync = (status: SyncStatus, extra: Partial<Extract<SyncState, { mode: 'cloud' }>> = {}) => {
    const prev = session.sync.get();
    const base = prev.mode === 'cloud' ? prev : { mode: 'cloud' as const, status, readOnly: false, unsynced: 0 };
    session.sync.set({ ...base, status, ...extra });
  };
  const isTerminal = () => {
    const cur = session.sync.get();
    return cur.mode === 'cloud' && TERMINAL.includes(cur.status);
  };

  /** Viewers get a read-only document; remembered in the registry so it holds offline too. */
  const applyRole = (role: Role) => {
    const readOnly = !roleAtLeast(role, 'editor');
    const was = session.project.readOnly;
    session.project.setReadOnly(readOnly);
    const cur = session.sync.get();
    if (cur.mode === 'cloud' && cur.readOnly !== readOnly) session.sync.set({ ...cur, readOnly });
    if (was && !readOnly) session.refreshIdentity();
    const entry = getRegistryEntry(session.id);
    if (entry?.cloud && entry.cloud.role !== role) void patchRegistry(session.id, { cloud: { ...entry.cloud, role } });
  };

  // Every attach starts clean: no status message carried over from a previous connection.
  session.sync.set({ mode: 'cloud', status: 'connecting', readOnly: session.project.readOnly, unsynced: 0 });
  const noop: CloudConnection = { destroy() {}, setLocation() {}, awareness: null };
  const auth = cloudStore.get().status;
  if (auth === 'unknown') return noop; // still checking the session; reattached right after
  if (auth === 'unavailable') {
    setSync('offline', { message: 'Tomo Cloud is unreachable. Changes are saved on this device and sync when it’s back.' });
    return noop;
  }
  if (auth !== 'signed-in') {
    setSync('signed-out', { message: 'Sign in to sync this project. Changes stay on this device meanwhile.' });
    return noop;
  }
  const ws = workspaceById(getRegistryEntry(session.id)?.cloud?.workspaceId);
  if (ws) applyRole(ws.role);

  let destroyed = false;
  let provider: YProvider | null = null;
  let location: string | null = session.location;
  const disposers: (() => void)[] = [];

  /** Plan, membership and deletion problems won't fix themselves by retrying. */
  const isFinal = (e: unknown) => e instanceof ApiRequestError && [401, 402, 403, 404].includes(e.status);

  const fail = (e: unknown) => {
    if (e instanceof ApiRequestError) {
      if (e.status === 402) setSync('upgrade-required', { message: 'This workspace needs a Pro plan to sync.' });
      else if (e.status === 401) setSync('signed-out');
      else if (e.status === 403 || e.status === 404) setSync('forbidden', { message: e.message });
      else if (e.status === 0) setSync('offline');
      else setSync('error', { message: e.message });
    } else setSync('error', { message: String(e) });
  };

  // Consecutive recoverable closes without a successful sync, for backing off.
  let recoveries = 0;
  let recoveryTimer: ReturnType<typeof setTimeout> | undefined;
  disposers.push(() => clearTimeout(recoveryTimer));

  /** The server closed the connection with a reason: reconnect, or show why and stop. */
  const onServerClose = (reason: string) => {
    if (isRecoverableSyncClose(reason)) {
      // Reconnect with a fresh token (and, after a role change, the new role). If that keeps
      // failing (say the API is down and no token can be had), back off instead of hammering.
      setSync('connecting');
      provider?.disconnect();
      const delay = recoveries === 0 ? 100 : Math.min(30_000, 500 * 2 ** (recoveries - 1));
      recoveries += 1;
      clearTimeout(recoveryTimer);
      recoveryTimer = setTimeout(() => {
        if (!destroyed && !isTerminal()) void provider?.connect().catch(() => undefined);
      }, delay);
      return;
    }
    const [status, message] = CLOSE_REASONS[reason] ?? ['error', reason || 'The connection was closed.'];
    setSync(status, { message });
    provider?.disconnect();
  };

  /** A fresh token for every (re)connect. The first one comes from the probe in `start()`. */
  let nextToken: string | null = null;
  const params = async (): Promise<Record<string, string>> => {
    if (nextToken) {
      const token = nextToken;
      nextToken = null;
      return { token };
    }
    try {
      const t = await api.projects.syncToken(session.id);
      applyRole(t.role);
      return { token: t.token };
    } catch (e) {
      fail(e);
      if (isFinal(e)) provider?.disconnect();
      // No token: an attempt still under way is refused and retried (or stays stopped).
      return { token: '' };
    }
  };

  const presenceFrom = (awareness: Awareness) => {
    const me = awareness.clientID;
    const list: PresenceUser[] = [];
    awareness.getStates().forEach((state, clientId) => {
      const user = state.user as { id?: string; name?: string; color?: string } | undefined;
      if (!user?.id) return;
      list.push({ clientId, id: user.id, name: user.name ?? 'Someone', color: user.color ?? '#999', location: (state.location as string | null) ?? null, self: clientId === me });
    });
    session.presence.set(list);
  };

  const uploadMissingBlobs = async () => {
    if (session.project.readOnly) return;
    try {
      const hashes = [...projectFileHashes(session.project)];
      if (!hashes.length) return;
      for (let i = 0; i < hashes.length; i += 200) {
        const { missing } = await api.blobs.check(session.id, hashes.slice(i, i + 200));
        for (const hash of missing) {
          if (destroyed) return;
          const blob = await getBlob(hash);
          if (blob) await api.blobs.upload(session.id, hash, blob);
        }
      }
    } catch {
      /* retried on the next change */
    }
  };

  const start = async () => {
    // Probe once so plan/permission problems surface as a clear state instead of endless retries.
    try {
      const t = await api.projects.syncToken(session.id);
      if (destroyed) return;
      applyRole(t.role);
      nextToken = t.token;
    } catch (e) {
      if (!destroyed) fail(e);
      return;
    }
    const base = new URL(API_BASE || window.location.origin);
    const p = new YProvider(base.host, session.id, session.doc, {
      prefix: syncPath(session.id),
      protocol: base.protocol === 'https:' ? 'wss' : 'ws',
      params,
      connect: false,
      // Each tab keeps its own connection, like before; no cross-tab channel.
      disableBc: true,
    });
    provider = p;

    p.on('status', ({ status }: { status: 'connecting' | 'connected' | 'disconnected' }) => {
      if (isTerminal()) return;
      if (status === 'connected') setSync(p.synced ? 'synced' : 'syncing', { message: undefined });
      else if (status === 'disconnected') setSync(navigator.onLine ? 'connecting' : 'offline');
      else setSync(navigator.onLine ? 'connecting' : 'offline');
    });
    p.on('sync', (synced: boolean) => {
      if (!synced || isTerminal()) return;
      recoveries = 0;
      setSync('synced', { message: undefined });
      void uploadMissingBlobs();
    });
    p.on('connection-close', (event: CloseEvent | null) => {
      if (event && event.code >= 4000 && event.code < 5000) onServerClose(event.reason);
    });

    const me = getIdentity();
    p.awareness.setLocalStateField('user', { id: me.id, name: me.name, color: me.color });
    p.awareness.setLocalStateField('location', location);
    // Deferred: awareness can change while React renders (the editor's caret plugin sets the
    // local user when it's created), and presence subscribers must not update mid-render.
    const onAwareness = () => queueMicrotask(() => presenceFrom(p.awareness));
    p.awareness.on('change', onAwareness);
    disposers.push(() => p.awareness.off('change', onAwareness));
    disposers.push(
      identityStore.subscribe(() => {
        const who = getIdentity();
        p.awareness.setLocalStateField('user', { id: who.id, name: who.name, color: who.color });
      }),
    );
    // Push new files after changes (debounced).
    let timer: ReturnType<typeof setTimeout> | undefined;
    disposers.push(
      session.project.subscribe(() => {
        clearTimeout(timer);
        timer = setTimeout(() => void uploadMissingBlobs(), 2500);
      }),
    );
    disposers.push(() => clearTimeout(timer));
    const online = () => {
      if (!isTerminal()) void p.connect().catch(() => undefined);
    };
    window.addEventListener('online', online);
    disposers.push(() => window.removeEventListener('online', online));

    void p.connect().catch(() => undefined);
  };

  disposers.push(registerRemoteBlobSource(session.id, (hash) => api.blobs.download(session.id, hash)));
  void start();

  return {
    get awareness() {
      return provider?.awareness ?? null;
    },
    setLocation(loc) {
      location = loc;
      provider?.awareness.setLocalStateField('location', loc);
    },
    destroy() {
      destroyed = true;
      disposers.forEach((d) => d());
      provider?.destroy();
      provider = null;
      session.presence.set([]);
    },
  };
}
